import { Check, AlertCircle } from 'lucide-react';
import { STATUS_STEPS, getStepIndex } from '@/lib/statusConfig';

const SPECIAL_STATUSES = {
  on_hold: { label: 'On Hold', color: 'amber' },
  action_needed: { label: 'Action Needed', color: 'amber' },
  payment_pending: { label: 'Awaiting Payment', color: 'amber' },
};

/**
 * JobStatusStepper — visual 7-step progress indicator.
 *
 * Props:
 *   status  {string}  — the job's client_status value
 *   compact {boolean} — dot-only mode for Dashboard cards (default: false)
 */
const JobStatusStepper = ({ status, compact = false }) => {
  const currentIndex = getStepIndex(status);
  const isSpecial = SPECIAL_STATUSES[status];

  if (compact) {
    return (
      <div className="flex items-center gap-1" aria-label={`Progress: step ${currentIndex + 1} of ${STATUS_STEPS.length}`}>
        {STATUS_STEPS.map((step, idx) => {
          const done = idx < currentIndex;
          const active = idx === currentIndex;
          return (
            <div
              key={step.key}
              title={step.label}
              className={[
                'rounded-full transition-all duration-200',
                active ? 'w-2.5 h-2.5 bg-primary ring-2 ring-primary/30' : 'w-2 h-2',
                done ? 'bg-primary/70' : !active ? 'bg-border' : '',
              ].join(' ')}
            />
          );
        })}
        {isSpecial && (
          <AlertCircle className="w-3 h-3 text-amber-500 ml-0.5" title={isSpecial.label} />
        )}
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="job-status-stepper">
      {/* On-hold / payment banner */}
      {isSpecial && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <AlertCircle className="w-4 h-4 shrink-0 text-amber-600" />
          <span className="font-medium">{isSpecial.label}</span>
          {(status === 'on_hold' || status === 'action_needed') && (
            <span className="text-amber-600">— our team will be in touch shortly</span>
          )}
        </div>
      )}

      {/* Step row */}
      <div className="flex items-start">
        {STATUS_STEPS.map((step, idx) => {
          const done = idx < currentIndex;
          const active = idx === currentIndex;
          const isLast = idx === STATUS_STEPS.length - 1;
          const isReview = step.key === 'ready_for_review';

          return (
            <div key={step.key} className="flex-1 flex flex-col items-center relative">
              {/* Connector line (left side) */}
              {idx > 0 && (
                <div
                  className={[
                    'absolute top-4 right-1/2 left-0 h-0.5 -translate-y-1/2',
                    done || active ? 'bg-primary' : 'bg-border',
                  ].join(' ')}
                />
              )}

              {/* Circle */}
              <div
                className={[
                  'relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold border-2 transition-all duration-300',
                  done
                    ? 'bg-primary border-primary text-primary-foreground'
                    : active && isReview
                    ? 'bg-amber-500 border-amber-500 text-white ring-4 ring-amber-200'
                    : active
                    ? 'bg-primary border-primary text-primary-foreground ring-4 ring-primary/20'
                    : 'bg-background border-border text-muted-foreground',
                ].join(' ')}
              >
                {done ? (
                  <Check className="w-4 h-4" strokeWidth={2.5} />
                ) : (
                  <span>{idx + 1}</span>
                )}
              </div>

              {/* Connector line (right side) */}
              {!isLast && (
                <div
                  className={[
                    'absolute top-4 left-1/2 right-0 h-0.5 -translate-y-1/2',
                    done ? 'bg-primary' : 'bg-border',
                  ].join(' ')}
                />
              )}

              {/* Label */}
              <p
                className={[
                  'mt-2 text-center text-xs leading-tight px-0.5 hidden sm:block',
                  active ? (isReview ? 'font-semibold text-amber-600' : 'font-semibold text-primary') : done ? 'text-muted-foreground' : 'text-muted-foreground/60',
                ].join(' ')}
              >
                {step.label}
                {active && isReview && (
                  <span className="block text-[10px] text-amber-500 mt-0.5">Action needed</span>
                )}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default JobStatusStepper;
